import {
  APP,
  PRODUCTS,
  PRODUCT_ARYA,
  PRODUCT_ASSETS,
  PRODUCT_ACTIVATE,
  PRODUCT_INTEGRATIONS,
  PRODUCT_LIST,
  STORAGE_UNITS
} from '@base-shared/utils/constants';

import type { components as GroupComponents } from '@base-shared/types/group.openapi';

export function isValidURL(url: string) {
  const regex = /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(:[0-9]{1,5})?(\/.*)?$/;

  return regex.test(url);
};

/**
 * Checks if the provided app is a valid, non-virtual product.
 */
export function isValidProduct(app: APP): boolean {
  return Object.values(PRODUCTS).includes(app as GroupComponents['schemas']['Product']);
}

/**
 * Converts an app to a list of products that should be enabled for it during SignUp
 */
export function getProductsToEnableForApp(app: APP): GroupComponents['schemas']['Product'][] {
  switch (app) {
    case APP.ARYA:
      return [PRODUCT_ARYA];
    case APP.ASSETS:
      return [PRODUCT_ASSETS];
    case APP.ACTIVATE:
      return [PRODUCT_ACTIVATE];
    case APP.INTEGRATIONS:
      // NOTE: this is the only exception b/c Integration doesn't make sense without
      // either ARYA or Assets
      return [PRODUCT_ASSETS, PRODUCT_INTEGRATIONS];

    // NOTE: HUB and HUB_SETTINGS are virtual products
    case APP.HUB:
    case APP.HUB_SETTINGS:
      return [PRODUCT_ASSETS];
    default:
      throw new Error(`Invalid app: ${app}`);
  }
};

/**
 * Open externalLink as a key from CONFIG.public.externalLinks
 * or as a raw URL
 */
export function openLink(value: string, isBlankTarget?: boolean) {
  if (!isValidURL(value)) {
    return;
  }

  if (isBlankTarget) {
    window?.open?.(value, '_blank')?.focus();
  } else {
    window.location.assign(value);
  }
}

export async function copyToClipboard(text: string) {
  if (!text) {
    console.warn('Nothing to copy');
    return;
  }

  try {
    await navigator.clipboard.writeText(text);
  } catch (err) {
    console.error('Failed to copy: ', err);
  }
};

/**
 * Utility function to get the product name from the PRODUCT_LIST
 * based on the product value.
 * @param products - The products to find the name for.
 * @return The list of converted names of the products or empty array.
 */
export function getProductNames(products: GroupComponents['schemas']['Product'][]): string[] {
  if (!products || !Array.isArray(products)) {
    return [];
  }

  return products
    .map((product) => PRODUCT_LIST.find((p) => p.value === product)?.name ?? '')
    .filter((name) => name);
}

/**
 * Checks if the current window is running on localhost.
 * @returns {boolean} - Returns true if the current window is running on localhost
 */
export function isWindowLocalhost(): boolean {
  if (typeof window === 'undefined') return false;

  return Boolean(
    window.location.hostname === 'localhost'
    // [::1] is the IPv6 localhost address.
    || window.location.hostname === '[::1]'
    // 127.0.0.1/8 is considered localhost for IPv4.
    || /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/.test(window.location.hostname)
  );
}

/**
 * Converts a storage value from one unit to another.
 *
 * @param value - The numeric value to convert.
 * @param fromUnit - The unit of the input value (e.g., 'B', 'KB', 'MB', 'GB', 'TB', 'PB').
 * @param toUnit - The unit to convert the value to (e.g., 'B', 'KB', 'MB', 'GB', 'TB', 'PB').
 * @param binary - If true, uses binary units (base 1024, e.g., KiB, MiB); if false, uses decimal units (base 1000).
 * @returns The converted value in the target unit.
 * @throws Will throw an error if either the fromUnit or toUnit is invalid.
 */
export function convertStorageUnits(value: number, fromUnit: string, toUnit: string, binary = false) {
  const base = binary ? 1024 : 1000; // binary (KiB, MiB...) vs decimal (kB, MB...)

  const fromIndex = STORAGE_UNITS.indexOf(fromUnit.toUpperCase() as typeof STORAGE_UNITS[number]);
  const toIndex = STORAGE_UNITS.indexOf(toUnit.toUpperCase() as typeof STORAGE_UNITS[number]);

  if (fromIndex === -1 || toIndex === -1) {
    throw new Error(`Invalid unit. Use one of: ${STORAGE_UNITS.join(', ')}.`);
  }

  // convert to bytes first
  const bytes = value * Math.pow(base, fromIndex);

  // then convert to target
  return bytes / Math.pow(base, toIndex);
}
